// SAD-461 (LIB-04) — Release & reuse panel on a Library card: publish a
// version, reuse it as a live reference / adopted copy / frozen snapshot, and
// inspect the lineage (mode, source version, withdrawn) of what a receiving
// object was based on. What each row shows is decided server-side for THIS
// viewer; a refusal appears as text, never as partial content.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { emptyProgramContext, ProgramContextEditor, readProgramContext, validProgramContext } from './ProgramContextEditor';
import { ELIGIBILITY_HELP, HistoryLink, ReleaseContext } from './ReleaseHistory';
import { libraryReleaseClient } from './reuseClient';
import { errorText, TRANSFER_MODES, useCreateReuse, useEditAdopted, useLibraryReuses, useReleaseItem, useSelectableReleases, useWithdrawRelease } from './reuseHooks';
import { SourceChangeNotice } from './SourceChangeNotice';
export function modeLabel(mode) {
    return TRANSFER_MODES.find((m)=>m.value === mode)?.label ?? mode;
}
export function ReusePanel({ itemId, title, preparedContent, programContext = false }) {
    const [open, setOpen] = useState(false);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "outline",
                onClick: ()=>setOpen((o)=>!o),
                "data-testid": Testids.libraryReuse.toggle,
                children: open ? 'Hide release & reuse' : 'Release & reuse'
            }),
            open && /*#__PURE__*/ _jsx(ReusePanelBody, {
                itemId: itemId,
                title: title,
                preparedContent: preparedContent,
                programContext: programContext
            })
        ]
    });
}
function ReusePanelBody({ itemId, title, preparedContent, programContext = false }) {
    const [reason, setReason] = useState('');
    const [savedWorking, setSavedWorking] = useState({
        title: '',
        content: ''
    });
    const [workingId, setWorkingId] = useState('');
    const [workingStatus, setWorkingStatus] = useState('');
    const [savingCorrection, setSavingCorrection] = useState(false);
    const [relTitle, setRelTitle] = useState(title);
    const [relContent, setRelContent] = useState(preparedContent);
    const typedContext = programContext && !!readProgramContext(relContent);
    const invalidContext = typedContext && !validProgramContext(relContent);
    const [eligibility, setEligibility] = useState('internal_only');
    const [externalRestrictions, setExternalRestrictions] = useState('');
    const [restrictions, setRestrictions] = useState('');
    const [withdrawReason, setWithdrawReason] = useState('');
    const [confirmWithdraw, setConfirmWithdraw] = useState(false);
    const [audience, setAudience] = useState('');
    const [releaseLine, setReleaseLine] = useState('');
    const [error, setError] = useState('');
    const [objectId, setObjectId] = useState('');
    const [mode, setMode] = useState('live_reference');
    const [canCorrect, setCanCorrect] = useState(false);
    const [ownedRelease, setOwnedRelease] = useState();
    const [workingReady, setWorkingReady] = useState(false);
    useEffect(()=>{
        let mounted = true;
        void libraryReleaseClient.getLibraryWorkingSource({
            libraryItemId: itemId
        }).then((v)=>{
            if (!mounted) return;
            setCanCorrect(true);
            setOwnedRelease(v.currentReleaseId ? {
                releaseId: v.currentReleaseId,
                version: v.currentReleaseVersion
            } : undefined);
            setWorkingId(v.workingVersionId);
            setSavedWorking({
                title: v.title,
                content: v.content
            });
            if (v.workingVersionId) {
                setRelTitle(v.title);
                setRelContent(v.content);
                setReason(v.correctionReason);
            }
        }).catch(()=>{
            if (mounted) setCanCorrect(false);
        }).finally(()=>{
            if (mounted) setWorkingReady(true);
        });
        return ()=>{
            mounted = false;
        };
    }, [
        itemId
    ]);
    const selectable = useSelectableReleases(true);
    const reuses = useLibraryReuses(objectId);
    const release = useReleaseItem();
    const withdraw = useWithdrawRelease();
    const createReuse = useCreateReuse();
    const mine = (selectable.isFetching || selectable.isError ? [] : selectable.data ?? []).filter((r)=>r.libraryItemId === itemId).sort((a, b)=>b.version - a.version);
    const current = mine[0];
    const run = async (fn)=>{
        setError('');
        try {
            await fn();
        } catch (e) {
            setError(errorText(e));
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4 rounded-md border p-3",
        "data-testid": Testids.libraryReuse.panel,
        children: [
            /*#__PURE__*/ _jsx(HistoryLink, {
                itemId: itemId
            }),
            !workingReady && /*#__PURE__*/ _jsx("output", {
                children: "Checking release access…"
            }),
            canCorrect && /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-sm font-medium",
                        children: "Release"
                    }),
                    !!workingId && (relTitle !== savedWorking.title || relContent !== savedWorking.content) && /*#__PURE__*/ _jsx("p", {
                        children: "Save working correction before releasing these edits."
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs",
                        children: "Save working correction keeps a draft. Release is a separate, deliberate publication to the audience below."
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        disabled: savingCorrection || release.isPending,
                        value: relTitle,
                        onChange: (e)=>setRelTitle(e.target.value),
                        placeholder: "Published title",
                        "data-testid": Testids.libraryReuse.releaseTitleInput
                    }),
                    typedContext ? /*#__PURE__*/ _jsx(ProgramContextEditor, {
                        content: relContent,
                        onChange: setRelContent,
                        disabled: savingCorrection || release.isPending
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            programContext && /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.programContext.prepare,
                                disabled: savingCorrection || release.isPending,
                                onClick: ()=>setRelContent(emptyProgramContext),
                                children: "Prepare Program context"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                disabled: savingCorrection || release.isPending,
                                value: relContent,
                                onChange: (e)=>setRelContent(e.target.value),
                                placeholder: "Published content",
                                "data-testid": Testids.libraryReuse.releaseContentInput
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        value: audience,
                        onChange: (e)=>setAudience(e.target.value),
                        placeholder: "Audience scope node ids, comma separated",
                        "data-testid": Testids.libraryReuse.releaseAudienceInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Restrictions",
                        htmlFor: "library-release-restrictions",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "library-release-restrictions",
                            value: restrictions,
                            onChange: (e)=>setRestrictions(e.target.value),
                            "data-testid": Testids.libraryHistory.restrictions
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Output eligibility",
                        htmlFor: "library-release-eligibility",
                        children: /*#__PURE__*/ _jsxs("select", {
                            id: "library-release-eligibility",
                            value: eligibility,
                            onChange: (e)=>setEligibility(e.target.value),
                            "data-testid": Testids.libraryHistory.eligibilitySelect,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "internal_only",
                                    children: "Internal only"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "external_eligible",
                                    children: "External eligible"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs",
                        children: [
                            ELIGIBILITY_HELP,
                            " Templates are starting structures, not proof of accomplishment."
                        ]
                    }),
                    eligibility === 'external_eligible' && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "External output restriction context (required)",
                        htmlFor: "library-external-restrictions",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "library-external-restrictions",
                            value: externalRestrictions,
                            onChange: (e)=>setExternalRestrictions(e.target.value),
                            "data-testid": Testids.libraryHistory.externalRestrictions
                        })
                    }),
                    canCorrect && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Correction reason",
                                htmlFor: "library-correction-reason",
                                children: /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "library-correction-reason",
                                    value: reason,
                                    onChange: (e)=>setReason(e.target.value),
                                    "data-testid": Testids.libraryChangeReview.correctionReason
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: savingCorrection || release.isPending || invalidContext || !reason.trim() || !relContent.trim(),
                                "data-testid": Testids.libraryChangeReview.saveCorrection,
                                onClick: ()=>void run(async ()=>{
                                        setSavingCorrection(true);
                                        try {
                                            const result = await libraryReleaseClient.correctLibrarySource({
                                                libraryItemId: itemId,
                                                title: relTitle,
                                                content: relContent,
                                                correctionReason: reason,
                                                expectedWorkingVersionId: workingId
                                            });
                                            setWorkingId(result.workingVersionId);
                                            setSavedWorking({
                                                title: relTitle,
                                                content: relContent
                                            });
                                            setWorkingStatus(`Working v${result.version} saved — not released`);
                                        } finally{
                                            setSavingCorrection(false);
                                        }
                                    }),
                                children: savingCorrection ? 'Saving…' : 'Save working correction'
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                "data-testid": Testids.libraryChangeReview.workingStatus,
                                children: workingStatus
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: release.isPending || invalidContext || typedContext && !workingId || savingCorrection || !workingReady || !!workingId && (relTitle !== savedWorking.title || relContent !== savedWorking.content) || !relTitle.trim() || !relContent.trim() || !audience.trim() || eligibility === 'external_eligible' && !externalRestrictions.trim(),
                                "data-testid": Testids.libraryReuse.releaseSubmitButton,
                                onClick: ()=>run(async ()=>{
                                        const r = await release.mutateAsync({
                                            externalOutputEligibility: eligibility,
                                            externalOutputRestrictions: eligibility === 'external_eligible' ? externalRestrictions.trim() : '',
                                            restrictions: restrictions.trim(),
                                            libraryItemId: itemId,
                                            workingVersionId: workingId,
                                            releasedTitle: relTitle,
                                            releasedContent: relContent,
                                            audienceScopeNodeIds: audience.split(',').map((s)=>s.trim()).filter(Boolean)
                                        });
                                        setOwnedRelease({
                                            releaseId: r.releaseId,
                                            version: r.version
                                        });
                                        setReleaseLine(`Released v${r.version}`);
                                    }),
                                children: release.isPending ? 'Releasing…' : 'Release'
                            }),
                            ownedRelease && /*#__PURE__*/ _jsxs(SpecraButton, {
                                size: "sm",
                                variant: "outline",
                                disabled: withdraw.isPending || release.isPending,
                                "data-testid": Testids.libraryReuse.withdrawButton,
                                onClick: ()=>setConfirmWithdraw((v)=>!v),
                                children: [
                                    "Withdraw v",
                                    ownedRelease.version
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs",
                                "data-testid": Testids.libraryReuse.releaseStatus,
                                children: releaseLine
                            })
                        ]
                    }),
                    confirmWithdraw && ownedRelease && /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2 rounded border p-3",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                children: "Withdrawal prevents new reuse and warns active adopters. Existing frozen text and history remain readable to currently permitted readers. It cannot recall exported files."
                            }),
                            /*#__PURE__*/ _jsx(SpecraFormField, {
                                label: "Withdrawal reason (required)",
                                htmlFor: "library-withdraw-reason",
                                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    id: "library-withdraw-reason",
                                    value: withdrawReason,
                                    disabled: withdraw.isPending,
                                    onChange: (e)=>setWithdrawReason(e.target.value),
                                    "data-testid": Testids.libraryHistory.withdrawReason
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !withdrawReason.trim() || withdraw.isPending,
                                "data-testid": Testids.libraryHistory.withdrawConfirm,
                                onClick: ()=>void run(async ()=>{
                                        await withdraw.mutateAsync({
                                            libraryItemId: itemId,
                                            releaseId: ownedRelease.releaseId,
                                            reason: withdrawReason.trim()
                                        });
                                        setConfirmWithdraw(false);
                                        setWithdrawReason('');
                                        setReleaseLine(`Withdrawn v${ownedRelease.version} — prevents new reuse`);
                                        setOwnedRelease(undefined);
                                    }),
                                children: withdraw.isPending ? 'Withdrawing…' : `Confirm withdrawal of v${ownedRelease.version}`
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                disabled: withdraw.isPending,
                                onClick: ()=>setConfirmWithdraw(false),
                                children: "Cancel"
                            })
                        ]
                    })
                ]
            }),
            current && /*#__PURE__*/ _jsx(ReleaseContext, {
                context: current.releaseContext
            }),
            selectable.isFetching && /*#__PURE__*/ _jsx("output", {
                children: "Loading permitted releases…"
            }),
            selectable.isError && /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    "Releases could not be loaded.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>void selectable.refetch(),
                        children: "Retry releases"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-sm font-medium",
                        children: "Reuse in a response"
                    }),
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        value: objectId,
                        onChange: (e)=>setObjectId(e.target.value),
                        placeholder: "Receiving response id",
                        "data-testid": Testids.libraryReuse.reuseObjectIdInput
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("select", {
                                className: "h-9 rounded-md border bg-background px-2 text-sm",
                                value: mode,
                                onChange: (e)=>setMode(e.target.value),
                                "data-testid": Testids.libraryReuse.reuseModeSelect,
                                children: TRANSFER_MODES.map((m)=>/*#__PURE__*/ _jsx("option", {
                                        value: m.value,
                                        children: m.label
                                    }, m.value))
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                size: "sm",
                                disabled: !current || !objectId || createReuse.isPending,
                                "data-testid": Testids.libraryReuse.reuseSubmitButton,
                                onClick: ()=>current && run(()=>createReuse.mutateAsync({
                                            releaseId: current.releaseId,
                                            mode,
                                            objectId
                                        })),
                                children: [
                                    "Use v",
                                    current?.version ?? '—'
                                ]
                            })
                        ]
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-destructive",
                "data-testid": Testids.libraryReuse.reuseError,
                children: error
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-sm font-medium",
                        children: "Lineage"
                    }),
                    reuses.isFetching && /*#__PURE__*/ _jsx("output", {
                        children: "Loading current reuse access…"
                    }),
                    reuses.isError && /*#__PURE__*/ _jsxs("p", {
                        role: "alert",
                        children: [
                            "Reuse is unavailable.",
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>void reuses.refetch(),
                                children: "Retry reuse"
                            })
                        ]
                    }),
                    objectId && !reuses.isFetching && !reuses.isError && !reuses.data?.length && /*#__PURE__*/ _jsx("p", {
                        children: "No permitted reuse is available for this response."
                    }),
                    (reuses.isFetching || reuses.isError ? [] : reuses.data ?? []).map((r)=>/*#__PURE__*/ _jsx(LineageRow, {
                            reuse: r,
                            onError: setError
                        }, r.reuseId))
                ]
            })
        ]
    });
}
export function LineageRow({ reuse, onError }) {
    const [draft, setDraft] = useState(reuse.content);
    const [unavailable, setUnavailable] = useState(false);
    useEffect(()=>{
        setDraft(reuse.content);
    }, [
        reuse.content
    ]);
    const edit = useEditAdopted();
    const withdrawn = reuse.status === 'source_withdrawn';
    if (unavailable) return /*#__PURE__*/ _jsx("p", {
        children: "Reuse is unavailable. Refresh to recheck access."
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1 rounded border p-2 text-xs",
        "data-testid": Testids.libraryReuse.lineageRow,
        "data-reuse-id": reuse.reuseId,
        "data-transfer-mode": reuse.transferMode,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-3",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        "data-testid": Testids.libraryReuse.lineageMode,
                        children: modeLabel(reuse.transferMode)
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        "data-testid": Testids.libraryReuse.lineageSource,
                        children: [
                            "Recorded from v",
                            reuse.sourceVersion
                        ]
                    }),
                    reuse.resolvedVersion > 0 && /*#__PURE__*/ _jsxs("span", {
                        "data-testid": Testids.libraryReuse.lineageResolved,
                        children: [
                            "Showing v",
                            reuse.resolvedVersion
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.libraryReuse.lineageStatus,
                        children: withdrawn ? 'Source withdrawn' : 'Source released'
                    })
                ]
            }),
            reuse.libraryItemId && /*#__PURE__*/ _jsx(HistoryLink, {
                itemId: reuse.libraryItemId
            }),
            /*#__PURE__*/ _jsx(ReleaseContext, {
                context: reuse.releaseContext
            }),
            withdrawn && /*#__PURE__*/ _jsx("output", {
                children: "Source withdrawn — prevents new reuse. Active adopters should review this warning. Existing frozen text and history remain readable to currently permitted readers; withdrawal cannot recall exported files."
            }),
            reuse.editable && /*#__PURE__*/ _jsx(SourceChangeNotice, {
                reuseId: reuse.reuseId,
                onAccepted: setDraft
            }),
            reuse.editable ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: draft,
                        onChange: (e)=>setDraft(e.target.value),
                        "data-testid": Testids.libraryReuse.adoptedEditInput
                    }),
                    /*#__PURE__*/ _jsxs(SpecraButton, {
                        size: "sm",
                        disabled: edit.isPending || draft === reuse.content,
                        "data-testid": Testids.libraryReuse.adoptedEditSave,
                        onClick: ()=>edit.mutateAsync({
                                reuseId: reuse.reuseId,
                                content: draft
                            }).catch(()=>{
                                setDraft('');
                                setUnavailable(true);
                                onError('Edits could not be saved, or access is unavailable. Refresh to recheck.');
                            }),
                        children: [
                            "Save edit (revision ",
                            reuse.revision + 1,
                            ")"
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsx("p", {
                className: "whitespace-pre-wrap",
                "data-testid": Testids.libraryReuse.lineageContent,
                children: reuse.content || 'No readable text is available.'
            })
        ]
    });
}
